import Vue from 'vue';
import { cloneDeep, result, pick } from 'lodash';
import { Message } from 'element-ui';
import i18n from '@/i18n';
import errorResolver from '@/helpers/errorResolver';
import { approveVisitValidation } from '@/shared/apis';
import {
  fetchSkuValidations, saveSkuValidation, createSkuValidation, fetchVisitSkuValidations
} from '../api';

export const EVENTS = {
  SHOW_TOOL_TIP: 'SHOW_TOOL_TIP',
  DESTROY_TOOLTIP: 'DESTROY_TOOLTIP',
  RENDER_SELECTED_CROP: 'RENDER_SELECTED_CROP',
  RE_RENDER_CROP: 'RE_RENDER_CROP',
  TOGGLE_EDIT_MODE: 'TOGGLE_EDIT_MODE',
  TOGGLE_SKU_BY_TYPE: 'TOGGLE_SKU_BY_TYPE',
  ZOOM_END: 'ZOOM_END',
  IMAGE_SET_CHANGED: 'IMAGE_SET_CHANGED'
};

export default new Vue({
  data() {
    return {
      ValidatedCrops: {
        isLoading: false,
        isLoaded: false,
        hasError: false,
        data: {
          body: []
        }
      },
      VisitSkuValidation: {
        isLoading: false,
        isLoaded: false,
        hasError: false,
        data: {}
      },
      edit_mode: false,
      validation_request: false,
      show_meta_sku: true,
      show_unrecognized_sku: true,
      show_normal_sku: true,
      show_non_sku: true,
      hide_competitor_sku: true,
      selected_crop: null,
      projectId: '',
      visitId: '',
      skuValidationId: 0,
      allow_approval: true
    };
  },
  methods: {
    selectCrop(item) {
      if (item.crop.crop_id === result(this.selected_crop, 'crop.crop_id')) {
        this.selected_crop = null;
        this.$emit(EVENTS.DESTROY_TOOLTIP);
      } else {
        this.selected_crop = cloneDeep(item);

        this.$emit(EVENTS.SHOW_TOOL_TIP, this.selected_crop);
      }
    },
    toggleSkuByType() {
      this.$emit(EVENTS.TOGGLE_SKU_BY_TYPE);
    },
    cancelSkuValidation() {
      let body = cloneDeep(this.ValidatedCrops.data.body);
      body = body.filter(item => item.crop_id !== this.selected_crop.crop.crop_id);

      return this.postSkuValidation({
        ...this.ValidatedCrops.data,
        body: JSON.stringify(body)
      });
    },
    applyObjection(target) {
      if (target) {
        const payload = {
          ...pick(this.selected_crop.crop, ['crop_id', 'boundaries', 'image_id', 'image_set_id', 'is_meta', 'is_unrecognized', 'is_validated']),
          ...pick(target, ['id'])
        };

        const body = cloneDeep(this.ValidatedCrops.data.body);
        const index = body.findIndex(item => item.crop_id === this.selected_crop.crop.crop_id);

        if (index !== -1) {
          body[index] = payload;
        } else {
          body.push(payload);
        }

        this.postSkuValidation({
          ...this.ValidatedCrops.data,
          body: JSON.stringify(body)
        });
      } else {
        Message({ type: 'error', message: i18n.t('You have to select a sku') });
      }
    },
    sealValidation() {
      let confirmationDialog;

      if (this.allow_approval) {
        confirmationDialog = this.$confirm(
          i18n.t('This visit will be sent to approve. This action can not be undone'),
          i18n.t('Confirmation'),
          {
            confirmButtonText: i18n.t('Send to approve'),
            cancelButtonText: i18n.t('Cancel')
          }
        ).then(() => this.postSkuValidation({
          ...this.ValidatedCrops.data,
          sealed: true
        })).then(() => {
          Message(i18n.t('Visit was sent to approve.'));
          window.location.reload();
        }).catch((err) => {
          Message(i18n.t('Visit couldn\'t sent to approve.'));

          return Promise.reject(err);
        });
      } else {
        confirmationDialog = this.$confirm(
          i18n.t('This visit will be sealed and approved. This action can not be undone'),
          i18n.t('Confirmation'),
          {
            confirmButtonText: i18n.t('Seal and approve'),
            cancelButtonText: i18n.t('Cancel')
          }
        )
          .then(() => this.postSkuValidation({
            ...this.ValidatedCrops.data,
            sealed: true
          }))
          .then(() => fetchVisitSkuValidations(this.projectId, this.visitId))
          .then(({ data: { data } }) => {
            this.VisitSkuValidation.data = data;

            if (data.sku_validation) {
              this.ValidatedCrops.data = data.sku_validation;
            }
          })
          .then(() => approveVisitValidation(this.projectId, this.visitId, this.VisitSkuValidation.data.sku_validation.id))
          .then(() => {
            Message(i18n.t('Visit was sealed and approved'));
            setTimeout(() => {
              window.location.reload();
            }, 1500);
          })
          .catch((err) => {
            this.ValidatedCrops.hasError = true;
            Message(i18n.t('Visit couldn\'t sealed and approved.'));

            return Promise.reject(err);
          });
      }

      return confirmationDialog;
    },
    postSkuValidation(sku_validation) {
      this.validation_request = true;

      return saveSkuValidation(this.projectId, this.visitId, this.ValidatedCrops.data.id, { sku_validation })
        .then(() => this.fetchSkuValidations())
        .then(() => {
          this.$emit(EVENTS.RE_RENDER_CROP);

          return Promise.resolve();
        })
        .finally(() => {
          this.validation_request = false;
        });
    },
    async fetchSkuValidations() {
      this.ValidatedCrops.isLoading = true;

      return fetchSkuValidations(this.projectId, this.visitId, this.ValidatedCrops.data.id)
        .then(({ data: { data } }) => {
          this.ValidatedCrops.data = data;
          Message(i18n.t('Validation saved'));

          return Promise.resolve(data);
        })
        .catch((err) => {
          this.ValidatedCrops.hasError = true;

          return Promise.reject(err);
        })
        .finally(() => {
          this.ValidatedCrops.isLoading = false;
        });
    },
    unlinkEditMode(target) {
      if (target) {
        const payload = {
          ...pick(this.selected_crop.crop, ['crop_id', 'boundaries', 'image_id', 'image_set_id', 'is_meta', 'is_unrecognized']),
          ...pick(target, ['id'])
        };
        const body = cloneDeep(this.ValidatedCrops.data.body);
        const index = body.findIndex(item => item.crop_id === this.selected_crop.crop.crop_id);
        const lastIndex = body.length;

        if (index !== -1) {
          body[index] = payload;
        } else {
          body.push(payload);
          body[lastIndex].id = 'undefined';
        }

        this.postSkuValidation({
          ...this.ValidatedCrops.data,
          body: JSON.stringify(body)
        });
      }
    },
    async emitEditMode() {
      try {
        if (!this.ValidatedCrops.data.id) {
          const { data: { data } } = await createSkuValidation(this.projectId, this.visitId);

          this.ValidatedCrops.data = data;
        }

        this.$emit(EVENTS.TOGGLE_EDIT_MODE, this.edit_mode);
        this.unlinkEditMode();
      } catch (error) {
        Message(errorResolver(error).message);
      }
    }
  }
});
